import { useState, useEffect } from 'react';
import { listEDASuggestions, updateEDASuggestion } from '../../api/analytics';

const SEVERITY_COLORS = {
  error:   { background: '#fee2e2', border: '#fca5a5', color: '#991b1b' },
  warning: { background: '#fffbeb', border: '#fcd34d', color: '#92400e' },
  info:    { background: '#eff6ff', border: '#93c5fd', color: '#1e40af' },
};

const CATEGORY_LABELS = {
  redundancy: 'Redundancy',
  weak_signal: 'Weak Signal',
  sparsity: 'Sparsity',
  imbalance: 'Imbalance',
  temporal: 'Temporal',
  topic: 'Topic',
};

export default function QualityEDAReport({ configId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);

  function load() {
    setLoading(true);
    listEDASuggestions(configId)
      .then(res => {
        const data = res.data ?? res;
        setSuggestions(Array.isArray(data) ? data : (data?.suggestions || []));
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Failed to load suggestions');
        setLoading(false);
      });
  }

  useEffect(() => { load(); }, [configId]);

  async function handleUpdate(sid, accepted) {
    setUpdating(sid);
    try {
      await updateEDASuggestion(configId, sid, { accepted });
      setSuggestions(prev => prev.map(s => s.id === sid ? { ...s, accepted } : s));
    } catch (err) {
      setError(err.message || 'Update failed');
    } finally {
      setUpdating(null);
    }
  }

  if (loading) return <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading suggestions…</div>;
  if (error) return <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</div>;
  if (!suggestions.length) return <div style={{ color: '#9ca3af', fontSize: '0.875rem', fontStyle: 'italic' }}>No suggestions. Run Quality EDA first.</div>;

  // Group by severity
  const grouped = { error: [], warning: [], info: [] };
  for (const s of suggestions) {
    (grouped[s.severity] || grouped.info).push(s);
  }

  return (
    <div>
      {['error', 'warning', 'info'].map(sev => grouped[sev].length > 0 && (
        <div key={sev} style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: SEVERITY_COLORS[sev].color, marginBottom: '6px', textTransform: 'capitalize' }}>
            {sev} ({grouped[sev].length})
          </div>
          {grouped[sev].map(s => (
            <div key={s.id} style={{ padding: '8px 10px', borderRadius: '6px', border: `1px solid ${SEVERITY_COLORS[sev].border}`, background: SEVERITY_COLORS[sev].background, marginBottom: '6px', fontSize: '0.8125rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{CATEGORY_LABELS[s.category] || s.category}: </span>
                  {s.message}
                  {s.suggestion && <div style={{ color: '#6b7280', marginTop: '2px' }}>{s.suggestion}</div>}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginLeft: '12px', flexShrink: 0 }}>
                  {s.accepted === null || s.accepted === undefined ? (
                    <>
                      <button onClick={() => handleUpdate(s.id, true)} disabled={updating === s.id}
                        style={{ padding: '2px 8px', borderRadius: '4px', border: '1px solid #059669', background: '#fff', color: '#059669', cursor: 'pointer', fontSize: '0.75rem' }}>
                        Accept
                      </button>
                      <button onClick={() => handleUpdate(s.id, false)} disabled={updating === s.id}
                        style={{ padding: '2px 8px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer', fontSize: '0.75rem' }}>
                        Dismiss
                      </button>
                    </>
                  ) : (
                    <span style={{ color: s.accepted ? '#059669' : '#9ca3af', fontSize: '0.75rem' }}>
                      {s.accepted ? '✓ Accepted' : '✗ Dismissed'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
